/**
 * Whether to prompt a brand-new player into basic training, and how that is known.
 *
 * The garage floor is the landing screen, so the first thing a player sees after
 * registering is a hangar with a tank in it, a shop of things they cannot yet
 * afford, and no indication that there is a tutorial at all — it is the first
 * card on the battle picker's Missions tab, which is two clicks behind a button
 * labelled *In battle*. A player who has never driven a tank is exactly the
 * player least likely to press that to find out. So they are asked once.
 *
 * ── "Brand new" is derived from the server, not stored ───────────────────────
 *
 * Both halves are already on the garage overview, so this needs no column, no
 * endpoint and no registration hook — which matters, because a hook is the
 * obvious way to do this and is the wrong one: rank is derived from lifetime XP
 * for the same reason, and *nothing anywhere fires when somebody registers*
 * either, so a "show the prompt" flag written at registration would be a fact
 * that could disagree with the account it describes.
 *
 * - `stats.sessionsJoined === 0` — they have never entered an arena. This is the
 *   half that makes the prompt self-clearing: deploy anywhere, tutorial or not,
 *   and it never appears again, because a player who has been in a battle does
 *   not need to be told battles exist.
 * - no `MissionRun` for `tutorial` — they have not already done it. Redundant
 *   with the above **today**, since finishing the tutorial requires joining it
 *   and joining it counts a session... except that it does not: the tutorial is
 *   the one join that never reaches `openSession`, so it increments nothing.
 *   That is exactly why both halves are here rather than one.
 *
 * ── Dismissal is a courtesy, and deliberately not the mechanism ──────────────
 *
 * A player who says *not now* is taken at their word for that browser, in
 * `localStorage` beside the bays and the map preference — per player, because two
 * accounts on one browser is normal. But losing that entry costs a player one
 * click, and the *real* stopping condition is the derived one above: the prompt
 * cannot outlive their first sortie whatever storage does. So a browser refusing
 * `localStorage` shows the prompt again on reload rather than breaking, and there
 * is nothing here that can strand a player in a dialog.
 */

/** Storage is keyed per player. Same rule, and same reason, as the bays. */
const KEY_PREFIX = 'sow.training-prompt-dismissed.';

/**
 * What the decision is made from — a deliberate subset of `GarageOverview`.
 *
 * A shape rather than the overview itself, so this is testable without building
 * one and so the two facts it reads are visible in the signature. `stats` is
 * nullable on the wire (an account with no stats row yet), and `null` is treated
 * as *has played nothing*, which is the honest reading: a player with no stats
 * has certainly not joined a session.
 */
export interface TrainingFacts {
  sessionsJoined: number | null;
  /** The `kind` of every mission this account has beaten, at any difficulty. */
  missionsBeaten: readonly string[];
}

/**
 * Has this account done anything at all yet?
 *
 * Pure, so the rule is one testable function rather than a condition spread over
 * a `v-if`. It answers only the *derived* half — dismissal is the caller's, and
 * keeping the two apart is what makes "the prompt cannot outlive the first
 * sortie" checkable on its own.
 */
export function needsTraining(facts: TrainingFacts): boolean {
  if ((facts.sessionsJoined ?? 0) > 0) return false;
  return !facts.missionsBeaten.includes('tutorial');
}

/** Whether this player has already waved the prompt away in this browser. */
export function dismissed(playerKey: string): boolean {
  try {
    return window.localStorage.getItem(KEY_PREFIX + playerKey) === '1';
  } catch {
    // A browser refusing storage behaves like a fresh one: the prompt shows.
    // That is the safe direction — the alternative is suppressing it for a
    // player who never dismissed anything.
    return false;
  }
}

/** Remembers a *not now*. Failure is silent: the prompt is not worth an error. */
export function dismiss(playerKey: string): void {
  try {
    window.localStorage.setItem(KEY_PREFIX + playerKey, '1');
  } catch {
    /* no storage, so the prompt returns on reload — see the note above */
  }
}
